import React, {useContext, useEffect, useMemo, useState} from "react";
import {Box, Button, Stack, Typography} from "@mui/material";
import {IconDeviceFloppy, IconPlus, IconUsers} from "@tabler/icons-react";
import {grey} from "@mui/material/colors";
import {toast} from "react-toastify";
import {Context as AppContext} from "../../../context/AppContext";
import {CreateMemberModal} from "../members/components/add-project-member";
import {ProjectMemberItem} from "../members/components/project-member-item";

/**
 * Members tab of Project Settings - same card/section chrome as the
 * General tab (icon-chip header, sticky Save/Reset bar) so switching
 * tabs doesn't feel like a different page.
 */
export const ProjectMembersSettings = ({project}) => {
  const {updateProject} = useContext(AppContext);
  const [addMember, setAddMember] = useState(false);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    setMembers(project?.members || []);
  }, [project?.members]);

  const handleSave = (e) => {
    e?.preventDefault();
    const vdnManager = project?.vdnManager || {};
    const team = members?.map(i => i?.username) || [];
    members?.forEach(member => {
      vdnManager[member?.username] = member?.vdn;
    })

    const newProject = {...project, members, team, vdnManager};
    try {
      updateProject({...newProject})
      toast.success("Project updated successfully")
    } catch (error) {
      toast.error(error?.message || "Failed to update project")
    }
  }

  const hasMemberChangesIncludingRemoved = (original, updated) => {
    const originalMap = Object.fromEntries(original.map(m => [m.username, m]));
    const updatedMap = Object.fromEntries(updated.map(m => [m.username, m]));

    const changed = updated.some(member => {
      const orig = originalMap[member.username];
      return !orig || orig.vdn !== member.vdn || orig.role !== member.role;
    });

    const removed = original.some(member => !updatedMap[member.username]);

    return changed || removed;
  };

  const hasChanges = useMemo(() => {
    return hasMemberChangesIncludingRemoved(project?.members || [], members);
  }, [members, project?.members])

  return (
    <>
      <CreateMemberModal open={addMember} handleClose={() => setAddMember(false)} project={project}/>
      <Box component={"form"} onSubmit={handleSave}>
        <Stack
          direction={{xs: "column", sm: "row"}}
          justifyContent={"space-between"}
          alignItems={{xs: "flex-start", sm: "center"}}
          gap={1.5}
          sx={{pt: {xs: 2, md: 3}, px: {xs: 2, md: 4}}}
        >
          <Stack direction={"row"} gap={1.5} alignItems={"flex-start"}>
            <Box sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 34,
              height: 34,
              borderRadius: 2,
              flexShrink: 0,
              bgcolor: "rgba(132, 145, 247, 0.12)",
              color: "primary.main"
            }}>
              <IconUsers size={18}/>
            </Box>
            <Box>
              <Typography fontWeight={600} fontSize={{xs: "1.1rem", md: "1.25rem"}}>Members</Typography>
              <Typography variant={"body2"} color={"text.secondary"} sx={{mt: 0.25}}>
                Project access is granted to members, each with a specific role.
              </Typography>
            </Box>
          </Stack>
          <Button
            type={"button"}
            onClick={() => setAddMember(true)}
            startIcon={<IconPlus size={18}/>}
            variant={"primary"}
            sx={{borderRadius: 2, flexShrink: 0, width: {xs: "100%", sm: "auto"}}}
          >
            Add Member
          </Button>
        </Stack>

        <Stack direction={"column"} gap={1.25} sx={{px: {xs: 2, md: 4}, py: {xs: 2, md: 3}}}>
          {members?.length
            ? members.map(i => <ProjectMemberItem key={i?.username} member={i} setMembers={setMembers}/>)
            : <Typography variant={"body2"} color={grey[500]} sx={{py: 2, textAlign: "center"}}>
              No members yet. Add your first team member.
            </Typography>
          }
        </Stack>

        <Stack
          direction={"row"}
          justifyContent={"flex-end"}
          gap={{xs: 1, sm: 2}}
          sx={{
            position: {xs: "sticky", md: "static"},
            bottom: 0,
            zIndex: 2,
            px: {xs: 2, md: 4},
            py: {xs: 1.5, md: 2},
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button
            type={"button"} variant={"ghost"} color={"secondary"} disabled={!hasChanges}
            onClick={() => setMembers(project?.members || [])}
            sx={{minHeight: {xs: 44, md: "auto"}, flex: {xs: 1, sm: "initial"}}}
          >
            Reset
          </Button>
          <Button
            type={"submit"} variant={"primary"} disabled={!hasChanges}
            sx={{minHeight: {xs: 44, md: "auto"}, flex: {xs: 1, sm: "initial"}, px: {xs: 3, md: 2.5}}}
            startIcon={<IconDeviceFloppy size={18}/>}
          >
            Save Changes
          </Button>
        </Stack>
      </Box>
    </>
  )
}
